// @ts-strict-ignore
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()({
  animboxHidden: {
    '& *': {
      animation: 'none !important',
    },
  },
})

export interface IAnimBoxProps {
  children: React.ReactNode
  delay?: number
  offset?: number
  grow?: boolean
  slide?: boolean
  style?: any
  heavySlide?: boolean
  heavySlideRight?: boolean
  heavySlideUp?: boolean
}

export default function AnimBox(props: IAnimBoxProps) {
  const { grow, offset = 0, delay = 0, slide, heavySlide, heavySlideRight, heavySlideUp } = props

  const [isShown, setIsShown] = useState<boolean>(false)
  const [isMounted, setIsMounted] = useState<boolean>(false)
  const [noAnim, setNoAnim] = useState<boolean>(false)

  const { classes } = useStyles()

  const ref = useRef<HTMLDivElement>(null)

  let style = {
    visibility: isShown ? 'visible' as 'visible' : 'hidden' as 'hidden',
  }

  if (props.style) {
    style = {
      ...props.style,
      ...style,
    }
  }

  let updatedProps = {
    ...props,
  }

  delete updatedProps.grow
  delete updatedProps.slide
  delete updatedProps.delay
  delete updatedProps.offset
  delete updatedProps.heavySlide
  delete updatedProps.heavySlideRight
  delete updatedProps.heavySlideUp

  const isHiddenByParent = (el) => {
    // eslint-disable-next-line no-cond-assign
    while (el && (el = el.parentElement)) {
      if (getComputedStyle(el).visibility === 'hidden') {
        return true
      }
    }

    return false
  }

  const animateWhenVisible = useCallback(() => {
    if (!isMounted || !ref.current) return

    let domElem = ref.current
    let domElemBounds = domElem.getBoundingClientRect()
    let appearThreshold = (window.innerHeight * 9) / 10
    let domCenterY = Math.min((domElemBounds.bottom + domElemBounds.top) / 2 + offset, domElemBounds.top + window.innerHeight / 3 + offset)

    if (!isHiddenByParent(domElem) && domCenterY < appearThreshold) {
      if (domCenterY - offset < 0) {
        setNoAnim(true)
      }

      setIsShown(true)
    } else {
      // Keep polling at 20 FPS
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            animateWhenVisible()
          })
        })
      })
    }
  }, [isMounted, offset])

  useEffect(() => {
    setIsMounted(true)

    if (window.innerWidth < 700) {
      setIsShown(true)
    } else {
      animateWhenVisible()
    }

    if (ref.current.animate && !noAnim) {
      if (grow) {
        ref.current.animate(
          [
            { opacity: 0, transform: 'scale(0)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          {
            duration: 500,
            delay,
            fill: 'backwards',
            easing: 'cubic-bezier(0.7,0,0,1)',
          },
        )
      } else if (slide) {
        ref.current.animate(
          [
            { opacity: 0, transform: 'translateX(-48px)' },
            { opacity: 1, transform: 'translateX(0)' },
          ],
          {
            duration: 500,
            delay,
            fill: 'backwards',
            easing: 'cubic-bezier(0,1,1,1)',
          },
        )
      } else if (heavySlide) {
        ref.current.animate(
          [
            { opacity: 0, transform: 'translateX(-1080px)' },
            { opacity: 1, transform: 'translateX(0)' },
          ],
          {
            duration: 500,
            delay,
            fill: 'backwards',
            easing: 'cubic-bezier(0,1,0.1,1)',
          },
        )
      } else if (heavySlideRight) {
        ref.current.animate(
          [
            { opacity: 0, transform: 'translateX(1080px)' },
            { opacity: 1, transform: 'translateX(0)' },
          ],
          {
            duration: 500,
            delay,
            fill: 'backwards',
            easing: 'cubic-bezier(0,1,0.1,1)',
          },
        )
      } else if (heavySlideUp) {
        ref.current.animate(
          [
            { opacity: 0, transform: 'translateY(640px)' },
            { opacity: 1, transform: 'translateY(0)' },
          ],
          {
            duration: 500,
            delay,
            fill: 'backwards',
            easing: 'cubic-bezier(0,1,0.1,1)',
          },
        )
      } else {
        ref.current.animate(
          [
            { opacity: 0 },
            { opacity: 1 },
          ],
          {
            duration: 350,
            delay,
            fill: 'backwards',
          },
        )
      }
    }

    return () => {
      setIsMounted(false)
    }
  }, [animateWhenVisible, delay, grow, isShown, noAnim, heavySlide, heavySlideRight, heavySlideUp, slide])

  return (
    <div
      ref={ref}
      {...updatedProps}
      style={style}
      className={isShown ? undefined : classes.animboxHidden}
    >
      {props.children}
    </div>
  )
}
